import React, { type ReactNode, useEffect, useId } from 'react';
import {
	APIProvider,
	Map as GoogleMap,
	useApiIsLoaded,
	useMap,
} from '@vis.gl/react-google-maps';

import Skiplink from 'components/Skiplink';
import { publicRuntimeConfig } from 'config';
import { cnm } from 'utils/classNames';
import { useI18n } from 'utils/i18n';

interface Marker {
	latitude: number;
	longitude: number;
}

function getBoundsForMarkers(markers: Marker[]) {
	const bounds = new google.maps.LatLngBounds();
	markers.forEach((marker) => {
		bounds.extend(new google.maps.LatLng(marker.latitude, marker.longitude));
	});
	return bounds.toJSON();
}

function setBounds(map: google.maps.Map, markers: Marker[]) {
	if (markers.length > 1) {
		map.fitBounds(getBoundsForMarkers(markers), 42);
	} else if (markers.length === 1 && markers[0]) {
		map.panTo(
			new google.maps.LatLng(markers[0].latitude, markers[0].longitude),
		);
	}
}

interface MapProps {
	children: ReactNode;
	className?: string;
	mapClassName?: string | undefined;
	markers: Marker[];
}

function Map({ children, className, mapClassName, markers }: MapProps) {
	const { t } = useI18n();
	const map = useMap();

	const apiIsLoaded = useApiIsLoaded();
	useEffect(() => {
		if (!apiIsLoaded || !map) return;
		setBounds(map, markers);
	}, [markers, map, apiIsLoaded]);

	const id = useId();
	const afterMapId = `after-map-${id}`;

	if (!apiIsLoaded) {
		return null;
	}
	const hasMultipleMarkers = markers && markers.length > 1;

	return (
		<div className={cnm('relative h-full w-full', className)}>
			<Skiplink
				target={afterMapId}
				text={t('map_skiplink_text')}
				className="left-0 top-0 z-10 focus:absolute"
			/>
			<GoogleMap
				defaultZoom={7}
				defaultBounds={
					hasMultipleMarkers ? getBoundsForMarkers(markers) : undefined
				}
				defaultCenter={
					hasMultipleMarkers
						? undefined
						: {
								lat: markers[0]?.latitude ?? 0,
								lng: markers[0]?.longitude ?? 0,
							}
				}
				mapId="jula-map"
				className={mapClassName}
			>
				{children}
			</GoogleMap>
			<div id={afterMapId} tabIndex={-1} />
		</div>
	);
}

Map.displayName = 'MapBase_Map';

export interface Props {
	children: ReactNode;

	className?: string;

	/** className, can be used to control the height of the map. */
	mapClassName?: string;

	markers: Marker[];
}

export default function MapBase({
	children,
	className,
	mapClassName = 'h-[400px]',
	markers,
}: Props) {
	const googleApiKey = publicRuntimeConfig?.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
	if (!googleApiKey) {
		return null;
	}

	return (
		<APIProvider apiKey={googleApiKey}>
			<Map markers={markers} className={className} mapClassName={mapClassName}>
				{children}
			</Map>
		</APIProvider>
	);
}
MapBase.displayName = 'MapBase';
